/**
 * Session Management Utility
 * Handles session timeouts, validation, and security
 */

import { auth } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import { logAuthEvent, EVENT_TYPES } from './auditLogger';

// Session configuration
const SESSION_CONFIG = {
  DEFAULT_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
  REMEMBER_ME_TIMEOUT: 7 * 24 * 60 * 60 * 1000, // 7 days
  WARNING_TIME: 5 * 60 * 1000, // Warn 5 minutes before expiry
  CHECK_INTERVAL: 60 * 1000, // Check every minute
};

const STORAGE_KEYS = {
  SESSION_START: 'session_start_time',
  REMEMBER_ME: 'remember_me',
  LAST_ACTIVITY: 'last_activity',
  SESSION_ID: 'session_id',
};

let sessionCheckInterval = null;
let activityListeners = [];

/**
 * Initialize session management
 * @param {boolean} rememberMe - Whether to use extended session timeout
 */
export const initializeSession = (rememberMe = false) => {
  const sessionId = generateSessionId();
  const now = Date.now();
  
  // Store session info
  sessionStorage.setItem(STORAGE_KEYS.SESSION_START, now.toString());
  sessionStorage.setItem(STORAGE_KEYS.REMEMBER_ME, rememberMe.toString());
  sessionStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now.toString());
  sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
  
  // Start session monitoring
  startSessionMonitoring(rememberMe);
  
  // Track user activity
  setupActivityTracking();
  
  // Log session start
  logAuthEvent(EVENT_TYPES.LOGIN_SUCCESS, 'Session initialized', {
    sessionId,
    rememberMe,
  });
};

/**
 * Generate unique session ID
 * @returns {string} Session ID
 */
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Start session monitoring
 * @param {boolean} rememberMe - Whether extended timeout is active
 */
const startSessionMonitoring = (rememberMe) => {
  // Clear existing interval
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval);
  }
  
  const timeout = rememberMe ? SESSION_CONFIG.REMEMBER_ME_TIMEOUT : SESSION_CONFIG.DEFAULT_TIMEOUT;
  
  sessionCheckInterval = setInterval(() => {
    checkSessionTimeout(timeout);
  }, SESSION_CONFIG.CHECK_INTERVAL);
};

/**
 * Check if session has timed out
 * @param {number} timeout - Session timeout in milliseconds
 */
const checkSessionTimeout = (timeout) => {
  const sessionStart = parseInt(sessionStorage.getItem(STORAGE_KEYS.SESSION_START) || '0', 10);
  const lastActivity = parseInt(sessionStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY) || '0', 10);
  const now = Date.now();
  
  // Check if session has expired
  const sessionAge = now - sessionStart;
  const inactivityTime = now - lastActivity;
  
  // Session expired
  if (sessionAge > timeout) {
    endSession('Session expired');
    return;
  }
  
  // Inactivity timeout (15 minutes of no activity)
  const INACTIVITY_TIMEOUT = 15 * 60 * 1000;
  if (inactivityTime > INACTIVITY_TIMEOUT) {
    endSession('Session expired due to inactivity');
    return;
  }
  
  // Warn user if approaching timeout
  const timeUntilExpiry = timeout - sessionAge;
  if (timeUntilExpiry <= SESSION_CONFIG.WARNING_TIME && timeUntilExpiry > 0) {
    const minutesLeft = Math.ceil(timeUntilExpiry / (60 * 1000));
    // Dispatch custom event for UI to show warning
    window.dispatchEvent(new CustomEvent('session-warning', {
      detail: { minutesLeft }
    }));
  }
};

/**
 * Track user activity
 */
const setupActivityTracking = () => {
  const updateActivity = () => {
    sessionStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
  };
  
  // Track various user activities
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  
  events.forEach((event) => {
    const listener = () => {
      updateActivity();
    };
    document.addEventListener(event, listener, { passive: true });
    activityListeners.push({ event, listener });
  });
};

/**
 * End session
 * @param {string} reason - Reason for ending session
 */
export const endSession = async (reason = 'User logout') => {
  // Clear session monitoring
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval);
    sessionCheckInterval = null;
  }
  
  // Remove activity listeners
  activityListeners.forEach(({ event, listener }) => {
    document.removeEventListener(event, listener);
  });
  activityListeners = [];
  
  // Clear session storage
  Object.values(STORAGE_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
  
  // Sign out from Firebase
  try {
    await signOut(auth);
    await logAuthEvent(EVENT_TYPES.LOGOUT, reason);
  } catch (error) {
    console.error('Error signing out:', error);
  }
};

/**
 * Extend session (called on user activity)
 */
export const extendSession = () => {
  sessionStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
};

/**
 * Check if session is valid
 * @returns {boolean} True if session is valid
 */
export const isSessionValid = () => {
  const sessionStart = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);
  const sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
  
  if (!sessionStart || !sessionId) {
    return false;
  }
  
  // Additional validation can be added here
  return true;
};

/**
 * Get session info
 * @returns {object} Session information
 */
export const getSessionInfo = () => {
  const sessionStart = parseInt(sessionStorage.getItem(STORAGE_KEYS.SESSION_START) || '0', 10);
  const lastActivity = parseInt(sessionStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY) || '0', 10);
  const rememberMe = sessionStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
  const sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
  
  const timeout = rememberMe ? SESSION_CONFIG.REMEMBER_ME_TIMEOUT : SESSION_CONFIG.DEFAULT_TIMEOUT;
  const sessionAge = Date.now() - sessionStart;
  const timeRemaining = timeout - sessionAge;
  
  return {
    sessionId,
    rememberMe,
    sessionStart: new Date(sessionStart),
    lastActivity: new Date(lastActivity),
    timeRemaining,
    isExpired: timeRemaining <= 0,
  };
};

/**
 * Cleanup on page unload
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // Don't end session on page unload, just clear interval
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval);
    }
  });
}

